import siteMetadata from './siteMetadata.json';

export interface SeoFaqEntry {
  question: string;
  answer: string;
}

export interface SeoConfig {
  path: string;
  title: string;
  description: string;
  canonicalUrl?: string;
  imageUrl: string;
  indexable: boolean;
  type: 'website' | 'article';
  /* Emitted as FAQPage structured data. Google has restricted FAQ rich
     results to government and health sites since 2023, so this buys machine
     readability rather than a richer snippet. */
  faq?: SeoFaqEntry[];
  /* Emitted as HowTo structured data. Google retired HowTo rich results in
     2023 as well, so like the FAQ this is for machine readers. */
  howTo?: {
    name: string;
    steps: { title: string; body: string }[];
  };
  article?: {
    headline: string;
    publishedAt: string;
    modifiedAt: string;
  };
}

const normalizePath = (pathname: string): string => {
  const withoutQuery = pathname.split(/[?#]/, 1)[0] || '/';
  if (withoutQuery === '/') return '/';
  return `/${withoutQuery.replace(/^\/+|\/+$/g, '')}`;
};

const absoluteUrl = (path: string): string =>
  path.startsWith('http') ? path : `${siteMetadata.siteUrl}${path.startsWith('/') ? path : `/${path}`}`;

export const resolveSeo = (pathname: string): SeoConfig => {
  const path = normalizePath(pathname);
  const route = siteMetadata.routes.find((candidate) => candidate.path === path);

  if (route) {
    return {
      ...route,
      canonicalUrl: absoluteUrl(path === '/' ? '/' : path),
      imageUrl: absoluteUrl(siteMetadata.defaultImage),
      type: 'website',
    };
  }

  const isPrivate = siteMetadata.privatePrefixes.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );

  return {
    path,
    title: isPrivate ? resolveSeo('/').title : 'Page not found | IMABIRD',
    description: isPrivate
      ? 'Private IMABIRD Stretch your content.'
      : 'The requested page could not be found.',
    imageUrl: absoluteUrl(siteMetadata.defaultImage),
    indexable: false,
    type: 'website',
  };
};

interface BlogPostSeoInput {
  title: string;
  excerpt?: string | null;
  slug: string;
  createdAt: string;
  updatedAt: string;
}

export const buildBlogPostSeo = (post: BlogPostSeoInput): SeoConfig => {
  const path = `/blog/${encodeURIComponent(post.slug)}`;
  return {
    path,
    title: `${post.title} | IMABIRD`,
    description:
      post.excerpt?.trim() ||
      `Read ${post.title}, a guide from IMABIRD about creating stronger short-form video.`,
    canonicalUrl: absoluteUrl(path),
    imageUrl: absoluteUrl(siteMetadata.defaultImage),
    indexable: true,
    type: 'article',
    article: {
      headline: post.title,
      publishedAt: post.createdAt,
      modifiedAt: post.updatedAt || post.createdAt,
    },
  };
};

export { siteMetadata };
